const TAB_COUNT_KEY = "allaria-tab-count";
const SESSION_ACTIVE_KEY = "allaria-session-active";
const LAST_ACTIVITY_KEY = "allaria-last-activity";
const TAB_CLOSED_EVENT = "allaria-tab-closed";
const RELOAD_FLAG_KEY = "allaria-reloading";
const AUTH_KEYS = [
  "allaria-auth",
  "allaria-user", 
  "serverDiff",
  "lastPath",
  "logout"
];

const generateTabId = () => `tab-${Date.now()}-${Math.random()}`;

class SessionManager {
  private tabId: string;
  private isUnloading: boolean = false;
  private isReloading: boolean = false;
  private heartbeatInterval: number | null = null;
  private readonly HEARTBEAT_INTERVAL = 1000;
  private readonly SESSION_TIMEOUT = 3000;
  private readonly RELOAD_GRACE_PERIOD = 500;

  constructor() {
    this.tabId = generateTabId();
    this.detectReload();
    this.checkAndInitialize();
    this.setupEventListeners();
    this.startHeartbeat();
  }

  private detectReload(): void {
    const reloadFlag = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (reloadFlag) {
      this.isReloading = true;
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    }
  }

  private checkAndInitialize(): void {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const currentCount = this.getTabCount();
    const now = Date.now();
    
    if (this.isReloading) {
      localStorage.setItem(TAB_COUNT_KEY, String(currentCount + 1));
      localStorage.setItem(SESSION_ACTIVE_KEY, "true");
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      return;
    }
    
    if (lastActivity && currentCount === 0) {
      const timeSinceLastActivity = now - parseInt(lastActivity, 10);
      
      if (timeSinceLastActivity > this.RELOAD_GRACE_PERIOD) {
        this.clearAuthData();
        localStorage.removeItem(SESSION_ACTIVE_KEY);
        localStorage.setItem(TAB_COUNT_KEY, "1");
      } else {
        localStorage.setItem(TAB_COUNT_KEY, "1");
        localStorage.setItem(SESSION_ACTIVE_KEY, "true");
      }
    } else {
      localStorage.setItem(TAB_COUNT_KEY, String(currentCount + 1));
      if (currentCount > 0) {
        localStorage.setItem(SESSION_ACTIVE_KEY, "true");
      }
    }
    
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }

  private decrementTabCount(): void {
    const currentCount = this.getTabCount();
    const newCount = Math.max(0, currentCount - 1);
    localStorage.setItem(TAB_COUNT_KEY, String(newCount));
    
    localStorage.setItem(TAB_CLOSED_EVENT, Date.now().toString());
    
    if (newCount === 0) {
      localStorage.removeItem(SESSION_ACTIVE_KEY);
    }
  }

  private getTabCount(): number {
    const count = localStorage.getItem(TAB_COUNT_KEY);
    return count ? parseInt(count, 10) : 0;
  }

  private clearAuthData(): void {
    AUTH_KEYS.forEach(key => localStorage.removeItem(key));
    localStorage.removeItem(TAB_COUNT_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = window.setInterval(() => {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      
      const tabCount = this.getTabCount();
      if (tabCount > 0) {
        localStorage.setItem(SESSION_ACTIVE_KEY, "true");
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private setupEventListeners(): void {
    const handleUnload = () => {
      if (!this.isUnloading) {
        this.isUnloading = true;
        
        sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');
        
        this.stopHeartbeat();
        this.decrementTabCount();

        localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    window.addEventListener("storage", (e) => {
      if (e.key === "logout" && e.newValue) {
        this.clearSession();
        window.location.reload();
        return;
      }
      
      if (e.key === TAB_CLOSED_EVENT) {
        const tabCount = this.getTabCount();
        const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
        
        if (tabCount === 0 && lastActivity) {
          const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
          
          if (timeSinceLastActivity > this.RELOAD_GRACE_PERIOD) {
            this.clearAuthData();
            localStorage.removeItem(SESSION_ACTIVE_KEY);
          }
        }
      }
    });
  }

  clearSession(): void {
    this.stopHeartbeat();
    this.clearAuthData();
    localStorage.removeItem(SESSION_ACTIVE_KEY);
    
    localStorage.setItem(TAB_COUNT_KEY, "0");
  }

  hasActiveSession(): boolean {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    
    if (!lastActivity) return false;
    
    const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
    return timeSinceLastActivity <= this.SESSION_TIMEOUT;
  }

  getTabId(): string {
    return this.tabId;
  }

  getActiveTabCount(): number {
    return this.getTabCount();
  }
}

export const sessionManager = new SessionManager();
